/**
 * Secure Storage Interface
 * Platform-agnostic interface for secure key storage
 */
export interface SecureStorageAdapter {
  /**
   * Store a value securely
   */
  setItem(key: string, value: string): Promise<void>;

  /**
   * Retrieve a value
   */
  getItem(key: string): Promise<string | null>;

  /**
   * Remove a value
   */
  removeItem(key: string): Promise<void>;

  /**
   * Check if a key exists
   */
  hasItem(key: string): Promise<boolean>;

  /**
   * Clear all stored values
   */
  clear(): Promise<void>;
}

/**
 * iOS Keychain Storage Adapter
 * Uses react-native-keychain for iOS Keychain access
 */
export class KeychainStorageAdapter implements SecureStorageAdapter {
  private serviceName: string;

  constructor(serviceName: string = 'com.zubari.wallet') {
    this.serviceName = serviceName;
  }

  async setItem(key: string, value: string): Promise<void> {
    // In React Native, use react-native-keychain
    // const Keychain = require('react-native-keychain');
    // await Keychain.setGenericPassword(key, value, { service: this.serviceName });

    // Placeholder for native implementation
    if (typeof global !== 'undefined' && (global as any).KeychainModule) {
      await (global as any).KeychainModule.setItem(this.serviceName, key, value);
    } else {
      throw new Error('Keychain not available on this platform');
    }
  }

  async getItem(key: string): Promise<string | null> {
    if (typeof global !== 'undefined' && (global as any).KeychainModule) {
      return (global as any).KeychainModule.getItem(this.serviceName, key);
    }
    throw new Error('Keychain not available on this platform');
  }

  async removeItem(key: string): Promise<void> {
    if (typeof global !== 'undefined' && (global as any).KeychainModule) {
      await (global as any).KeychainModule.removeItem(this.serviceName, key);
    } else {
      throw new Error('Keychain not available on this platform');
    }
  }

  async hasItem(key: string): Promise<boolean> {
    const value = await this.getItem(key);
    return value !== null;
  }

  async clear(): Promise<void> {
    if (typeof global !== 'undefined' && (global as any).KeychainModule) {
      await (global as any).KeychainModule.clear(this.serviceName);
    } else {
      throw new Error('Keychain not available on this platform');
    }
  }
}

/**
 * Android Keystore Storage Adapter
 * Uses Android Keystore for secure storage
 */
export class KeystoreStorageAdapter implements SecureStorageAdapter {
  private alias: string;

  constructor(alias: string = 'zubari_wallet_keys') {
    this.alias = alias;
  }

  async setItem(key: string, value: string): Promise<void> {
    // In React Native, use react-native-encrypted-storage or similar
    // const EncryptedStorage = require('react-native-encrypted-storage');
    // await EncryptedStorage.setItem(key, value);

    if (typeof global !== 'undefined' && (global as any).KeystoreModule) {
      await (global as any).KeystoreModule.setItem(this.alias, key, value);
    } else {
      throw new Error('Keystore not available on this platform');
    }
  }

  async getItem(key: string): Promise<string | null> {
    if (typeof global !== 'undefined' && (global as any).KeystoreModule) {
      return (global as any).KeystoreModule.getItem(this.alias, key);
    }
    throw new Error('Keystore not available on this platform');
  }

  async removeItem(key: string): Promise<void> {
    if (typeof global !== 'undefined' && (global as any).KeystoreModule) {
      await (global as any).KeystoreModule.removeItem(this.alias, key);
    } else {
      throw new Error('Keystore not available on this platform');
    }
  }

  async hasItem(key: string): Promise<boolean> {
    const value = await this.getItem(key);
    return value !== null;
  }

  async clear(): Promise<void> {
    if (typeof global !== 'undefined' && (global as any).KeystoreModule) {
      await (global as any).KeystoreModule.clear(this.alias);
    } else {
      throw new Error('Keystore not available on this platform');
    }
  }
}

/**
 * Web Encrypted LocalStorage Adapter
 * Uses Web Crypto API for encryption with localStorage
 */
export class WebEncryptedStorageAdapter implements SecureStorageAdapter {
  private encryptionKey: CryptoKey | null = null;
  private storagePrefix: string;

  constructor(storagePrefix: string = 'zubari_') {
    this.storagePrefix = storagePrefix;
  }

  /**
   * Initialize with a password-derived key
   */
  async initialize(password: string): Promise<void> {
    const encoder = new TextEncoder();
    const salt = this.getSalt();

    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    this.encryptionKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt.buffer as ArrayBuffer,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private getSalt(): Uint8Array {
    const saltKey = `${this.storagePrefix}salt`;
    let saltHex = localStorage.getItem(saltKey);

    if (!saltHex) {
      const salt = crypto.getRandomValues(new Uint8Array(16));
      saltHex = Array.from(salt)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      localStorage.setItem(saltKey, saltHex);
    }

    return new Uint8Array(
      saltHex.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16))
    );
  }

  async setItem(key: string, value: string): Promise<void> {
    if (!this.encryptionKey) {
      throw new Error('Storage not initialized. Call initialize() first.');
    }

    const encoder = new TextEncoder();
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      this.encryptionKey,
      encoder.encode(value)
    );

    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    const base64 = btoa(String.fromCharCode(...combined));
    localStorage.setItem(`${this.storagePrefix}${key}`, base64);
  }

  async getItem(key: string): Promise<string | null> {
    if (!this.encryptionKey) {
      throw new Error('Storage not initialized. Call initialize() first.');
    }

    const base64 = localStorage.getItem(`${this.storagePrefix}${key}`);
    if (!base64) return null;

    try {
      const combined = new Uint8Array(
        atob(base64)
          .split('')
          .map((c) => c.charCodeAt(0))
      );

      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        this.encryptionKey,
        encrypted
      );

      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch {
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(`${this.storagePrefix}${key}`);
  }

  async hasItem(key: string): Promise<boolean> {
    return localStorage.getItem(`${this.storagePrefix}${key}`) !== null;
  }

  async clear(): Promise<void> {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.storagePrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
}

/**
 * In-Memory Storage Adapter (for testing)
 */
export class MemoryStorageAdapter implements SecureStorageAdapter {
  private storage: Map<string, string> = new Map();

  async setItem(key: string, value: string): Promise<void> {
    this.storage.set(key, value);
  }

  async getItem(key: string): Promise<string | null> {
    return this.storage.get(key) || null;
  }

  async removeItem(key: string): Promise<void> {
    this.storage.delete(key);
  }

  async hasItem(key: string): Promise<boolean> {
    return this.storage.has(key);
  }

  async clear(): Promise<void> {
    this.storage.clear();
  }
}

/**
 * Secure Storage Factory
 * Creates appropriate storage adapter based on platform
 */
export function createSecureStorage(): SecureStorageAdapter {
  // Check for React Native
  if (
    typeof global !== 'undefined' &&
    (global as any).nativeModuleProxy !== undefined
  ) {
    // Check platform
    const Platform = (global as any).Platform;
    if (Platform?.OS === 'ios') {
      return new KeychainStorageAdapter();
    } else if (Platform?.OS === 'android') {
      return new KeystoreStorageAdapter();
    }
  }

  // Check for browser environment
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    return new WebEncryptedStorageAdapter();
  }

  // Fallback to memory storage (for Node.js/testing)
  return new MemoryStorageAdapter();
}
